import { listZipEntries, readZipEntry, readZipText, ZipError } from './zip';
import { extensionOf } from './prepare';

/**
 * Reading a slide deck or a document well enough to study from it.
 *
 * The Library shows PDFs and images as they are. Everything else it now
 * accepts — PowerPoint, Word, OpenDocument, plain text — is shown as its
 * **text**: the words, in order, grouped by slide or by section. Not the
 * layout, not the diagrams, not the animations.
 *
 * That is a deliberate limit rather than a half-finished one. Laying out a
 * .pptx faithfully means reimplementing PowerPoint; what a reader actually
 * needs from his professor's slides at eleven at night is what they say. Where
 * the layout matters, exporting to PDF still gives the real thing, and the
 * app says so.
 *
 * Nothing here interprets markup. Text comes out of the XML as text and is
 * rendered as text — never as HTML, never evaluated.
 */

export type DocumentKind =
  | 'pdf'
  | 'image'
  | 'pptx'
  | 'docx'
  | 'odp'
  | 'odt'
  | 'text'
  /** Stored and synced, but nothing here can read it: the pre-2007 binaries. */
  | 'opaque';

const BY_MIME: Record<string, DocumentKind> = {
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.ms-powerpoint': 'opaque',
  'application/msword': 'opaque',
  'application/rtf': 'opaque',
  'text/plain': 'text',
  'text/markdown': 'text',
  'text/csv': 'text',
};

export function documentKind(mime: string, name = ''): DocumentKind {
  if (mime.startsWith('image/')) return 'image';
  const byMime = BY_MIME[mime];
  if (byMime) return byMime;
  // A phone that does not know .pptx sends an empty type; the name still does.
  const byExtension: Record<string, DocumentKind> = {
    pdf: 'pdf',
    pptx: 'pptx',
    docx: 'docx',
    odp: 'odp',
    odt: 'odt',
    ppt: 'opaque',
    doc: 'opaque',
    rtf: 'opaque',
    txt: 'text',
    md: 'text',
    csv: 'text',
  };
  return byExtension[extensionOf(name)] ?? 'opaque';
}

/** True for the kinds this module can turn into readable text. */
export function isReadable(kind: DocumentKind): boolean {
  return kind === 'pptx' || kind === 'docx' || kind === 'odp' || kind === 'odt' || kind === 'text';
}

export interface DocumentImage {
  /** The entry's path inside the archive, which is its identity. */
  name: string;
  mime: string;
  bytes: Uint8Array;
}

export interface DocumentSection {
  /** "Slide 4", "Page 2", or empty for a document that has no divisions. */
  title: string;
  lines: string[];
  /** Pictures placed on this slide, in the order it places them. */
  images: DocumentImage[];
}

export interface DocumentText {
  sections: DocumentSection[];
  /** Said out loud in the viewer, so nobody wonders what they are not seeing. */
  note: string;
}

function parseXml(xml: string): Document {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new ZipError('The text inside that file could not be read.');
  }
  return doc;
}

/**
 * Every element with this tag name, prefix and all.
 *
 * `getElementsByTagName` on an XML document matches the qualified name, which
 * is what the OOXML and ODF files actually write: `<a:t>`, `<w:t>`, `<text:p>`.
 */
function tagged(doc: Document | Element, name: string): Element[] {
  return [...doc.getElementsByTagName(name)];
}

const collapse = (text: string): string => text.replace(/\s+/g, ' ').trim();

/** The numbers in "ppt/slides/slide10.xml", so slide 10 follows slide 9. */
function slideNumber(path: string): number {
  const match = /(\d+)\.xml$/.exec(path);
  return match ? Number(match[1]) : 0;
}

const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
};

function imageMime(path: string): string | null {
  const dot = path.lastIndexOf('.');
  return dot === -1 ? null : (IMAGE_TYPES[path.slice(dot + 1).toLowerCase()] ?? null);
}

/**
 * Which file each picture on a slide refers to.
 *
 * A slide names its pictures by relationship id, and the ids are resolved in a
 * sibling file. Targets are written relative to the slide, so "../media/x.png"
 * has to be walked back to "ppt/media/x.png".
 */
function relationshipTargets(rels: string | undefined): Map<string, string> {
  const map = new Map<string, string>();
  if (!rels) return map;
  for (const node of tagged(parseXml(rels), 'Relationship')) {
    const id = node.getAttribute('Id');
    const target = node.getAttribute('Target');
    if (!id || !target) continue;
    map.set(id, `ppt/${target.replace(/^(\.\.\/)+/, '')}`);
  }
  return map;
}

const SLIDE_PATH = /^ppt\/slides\/slide\d+\.xml$/;
const SLIDE_RELS_PATH = /^ppt\/slides\/_rels\/slide\d+\.xml\.rels$/;

async function readPptx(bytes: Uint8Array): Promise<DocumentText> {
  const files = await readZipText(
    bytes,
    (name) => SLIDE_PATH.test(name) || SLIDE_RELS_PATH.test(name),
  );
  const paths = [...files.keys()]
    .filter((name) => SLIDE_PATH.test(name))
    .sort((a, b) => slideNumber(a) - slideNumber(b));

  const entries = listZipEntries(bytes);
  const sections: DocumentSection[] = [];

  for (const [index, path] of paths.entries()) {
    const doc = parseXml(files.get(path)!);

    // Each <a:p> is a paragraph; the <a:t> runs inside it are its words, split
    // wherever the formatting changes, so they are joined back up.
    const lines = tagged(doc, 'a:p')
      .map((paragraph) => collapse(tagged(paragraph, 'a:t').map((t) => t.textContent ?? '').join('')))
      .filter(Boolean);

    const targets = relationshipTargets(
      files.get(`ppt/slides/_rels/slide${slideNumber(path)}.xml.rels`),
    );

    const images: DocumentImage[] = [];
    const seen = new Set<string>();
    for (const blip of tagged(doc, 'a:blip')) {
      const id = blip.getAttribute('r:embed') ?? blip.getAttribute('embed');
      const target = id ? targets.get(id) : undefined;
      if (!target || seen.has(target)) continue;
      const entry = entries.get(target);
      const mime = imageMime(target);
      if (!entry || !mime) continue;
      seen.add(target);
      images.push({ name: target, mime, bytes: await readZipEntry(bytes, entry) });
    }

    sections.push({ title: `Slide ${index + 1}`, lines, images });
  }

  const pictures = sections.reduce((n, section) => n + section.images.length, 0);

  return {
    sections,
    note: pictures
      ? 'The slides as text and pictures. Layout, animations and anything drawn with shapes are not shown — export the deck to PDF if you need those.'
      : 'The text of the slides. Layout and anything drawn with shapes are not shown — export the deck to PDF if you need those.',
  };
}

async function readDocx(bytes: Uint8Array): Promise<DocumentText> {
  const files = await readZipText(bytes, (name) => name === 'word/document.xml');
  const xml = files.get('word/document.xml');
  if (!xml) throw new ZipError('That file has no document inside it.');

  const doc = parseXml(xml);
  const lines = tagged(doc, 'w:p')
    .map((paragraph) => collapse(tagged(paragraph, 'w:t').map((t) => t.textContent ?? '').join('')))
    .filter(Boolean);

  return {
    sections: [{ title: '', lines, images: [] }],
    note: 'The text of the document. Images, tables and layout are not shown — export to PDF if you need those.',
  };
}

async function readOpenDocument(bytes: Uint8Array, slides: boolean): Promise<DocumentText> {
  const files = await readZipText(bytes, (name) => name === 'content.xml');
  const xml = files.get('content.xml');
  if (!xml) throw new ZipError('That file has no content inside it.');

  const doc = parseXml(xml);
  const paragraphText = (scope: Document | Element): string[] =>
    tagged(scope, 'text:p')
      .map((p) => collapse(p.textContent ?? ''))
      .filter(Boolean);

  if (slides) {
    const pages = tagged(doc, 'draw:page');
    return {
      sections: pages.map((page, index) => ({
        title: `Slide ${index + 1}`,
        lines: paragraphText(page),
        images: [],
      })),
      note: 'The text of the slides. Diagrams, images and layout are not shown.',
    };
  }

  return {
    sections: [{ title: '', lines: paragraphText(doc), images: [] }],
    note: 'The text of the document. Images, tables and layout are not shown.',
  };
}

function readPlainText(bytes: Uint8Array): DocumentText {
  const text = new TextDecoder().decode(bytes);
  return {
    sections: [{ title: '', lines: text.split(/\r?\n/), images: [] }],
    note: '',
  };
}

/** The words in a document, grouped by slide or section. */
export async function readDocumentText(
  bytes: Uint8Array,
  kind: DocumentKind,
): Promise<DocumentText> {
  switch (kind) {
    case 'pptx':
      return readPptx(bytes);
    case 'docx':
      return readDocx(bytes);
    case 'odp':
      return readOpenDocument(bytes, true);
    case 'odt':
      return readOpenDocument(bytes, false);
    case 'text':
      return readPlainText(bytes);
    default:
      throw new ZipError('This kind of file cannot be shown here.');
  }
}

/** Why an opaque file cannot be shown, in one line a person can act on. */
export function opaqueReason(name: string): string {
  const extension = extensionOf(name);
  if (extension === 'ppt' || extension === 'doc') {
    return `.${extension} is the pre-2007 format, which stores its text in a way nothing in a browser can read. Open it once in PowerPoint or Word and save it as .${extension}x, or export it to PDF, and it will show here.`;
  }
  if (extension === 'rtf') {
    return 'Rich text is stored, but not shown here. Export it to PDF and it will open in the app.';
  }
  return 'This file is stored and synced, but the app cannot show what is inside it.';
}
